import React, { useState } from 'react';
import { Send, Plus, Trash2, Clock } from 'lucide-react';
import { useProxyRequest } from '@/hooks/useApi';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Notice } from '@/components/ui/Notice';
import { fieldControl, fieldTextarea } from '@/components/ui/fieldStyles';
import type { ProxyRequest, ProxyResponse } from '@/types';

interface HistoryEntry {
  id: number;
  request: ProxyRequest;
  response: ProxyResponse;
  timestamp: Date;
}

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;

const ProxyPage: React.FC = () => {
  const [method, setMethod] = useState<ProxyRequest['method']>('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([
    { key: '', value: '' },
  ]);
  const [body, setBody] = useState('');
  const [response, setResponse] = useState<ProxyResponse | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const proxyRequest = useProxyRequest();
  const showBody = ['POST', 'PUT', 'PATCH'].includes(method);

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    const updated = [...headers];
    updated[index][field] = val;
    setHeaders(updated);
  };

  const handleSend = async () => {
    if (!url.trim()) return;

    const headersObj: Record<string, string> = {};
    headers.forEach((h) => {
      if (h.key.trim()) headersObj[h.key.trim()] = h.value;
    });

    const request: ProxyRequest = {
      method,
      url: url.trim(),
      headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
      body: showBody && body.trim() ? body.trim() : undefined,
    };

    try {
      const result = await proxyRequest.mutateAsync(request);
      setResponse(result);
      setHistory((prev) => [
        { id: Date.now(), request, response: result, timestamp: new Date() },
        ...prev.slice(0, 9),
      ]);
    } catch {
      // Handled below via proxyRequest.isError / proxyRequest.error; this
      // catch just stops the mutation rejection from becoming an unhandled
      // promise rejection.
    }
  };

  const loadFromHistory = (entry: HistoryEntry) => {
    setMethod(entry.request.method);
    setUrl(entry.request.url);
    if (entry.request.headers) {
      setHeaders(
        Object.entries(entry.request.headers).map(([key, value]) => ({ key, value }))
      );
    }
    setBody(entry.request.body || '');
    setResponse(entry.response);
  };

  const statusTone = (status: number): BadgeTone => {
    if (status < 300) return 'success';
    if (status < 400) return 'warning';
    return 'error';
  };

  return (
    <div className="space-y-6">
      <PageHeader title="API proxy testing" description="Send a test call through the proxy and see the response straight away." />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          <Panel>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                aria-label="Method"
                value={method}
                onChange={(e) => setMethod(e.target.value as ProxyRequest['method'])}
                className={cn(fieldControl, 'font-mono font-medium sm:w-28')}
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <div className="min-w-0 flex-1">
                <Input
                  aria-label="URL"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://api.example.com/endpoint"
                  className="font-mono text-sm"
                />
              </div>
              <Button
                className="h-11"
                onClick={handleSend}
                loading={proxyRequest.isLoading}
                disabled={!url.trim()}
                leftIcon={<Send className="h-4 w-4" />}
              >
                Send
              </Button>
            </div>
          </Panel>

          <Panel
            title="Headers"
            action={
              <Button size="sm" variant="ghost" onClick={addHeader} leftIcon={<Plus className="h-4 w-4" />}>
                Add header
              </Button>
            }
          >
            <div className="space-y-2">
              {headers.length === 0 && <p className="text-sm text-muted-foreground">No headers. Add one if the API needs it.</p>}
              {headers.map((header, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <Input
                      aria-label={`Header ${i + 1} name`}
                      value={header.key}
                      onChange={(e) => updateHeader(i, 'key', e.target.value)}
                      placeholder="Header name"
                      className="font-mono text-sm"
                    />
                    <Input
                      aria-label={`Header ${i + 1} value`}
                      value={header.value}
                      onChange={(e) => updateHeader(i, 'value', e.target.value)}
                      placeholder="Value"
                      className="font-mono text-sm"
                    />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeHeader(i)} title="Remove header" aria-label="Remove header">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Panel>

          {showBody && (
            <Panel title="Request body (JSON)">
              <textarea
                aria-label="Request body (JSON)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder='{"key": "value"}'
                rows={8}
                className={cn(fieldTextarea, 'font-mono text-sm')}
              />
            </Panel>
          )}

          {proxyRequest.isError && (
            <Notice tone="error" title="Request failed">{getErrorMessage(proxyRequest.error)}</Notice>
          )}

          {response && (
            <Panel
              title="Response"
              action={
                <div className="flex items-center gap-3">
                  <Badge tone={statusTone(response.status)} className="font-mono">
                    {response.status} {response.statusText}
                  </Badge>
                  <span className="flex items-center font-mono text-xs tabular-nums text-muted-foreground">
                    <Clock className="mr-1 h-3 w-3" aria-hidden="true" />
                    {response.duration}ms
                  </span>
                </div>
              }
            >
              {response.headers && Object.keys(response.headers).length > 0 && (
                <div className="mb-4">
                  <h3 className="mb-2 text-xs font-medium text-muted-foreground">Response headers</h3>
                  <div className="space-y-1 overflow-x-auto rounded-xl border border-border bg-background p-3 font-mono text-xs">
                    {Object.entries(response.headers).map(([key, val]) => (
                      <div key={key} className="break-all">
                        <span className="text-muted-foreground">{key}:</span>{' '}
                        <span className="text-foreground">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h3 className="mb-2 text-xs font-medium text-muted-foreground">Body</h3>
                <pre className="max-h-96 overflow-auto rounded-xl border border-border bg-background p-4 font-mono text-[13px] text-foreground">
                  {typeof response.data === 'string'
                    ? response.data
                    : JSON.stringify(response.data, null, 2)}
                </pre>
              </div>
            </Panel>
          )}
        </div>

        <Panel title="Request history" className="h-fit" bodyClassName="px-2 pb-2">
          {history.length === 0 ? (
            <p className="px-3 pb-3 text-sm text-muted-foreground">No requests yet. Calls you send appear here.</p>
          ) : (
            <ul className="space-y-0.5">
              {history.map((entry) => (
                <li key={entry.id}>
                  <button
                    onClick={() => loadFromHistory(entry)}
                    className="w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-surface-raised"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium text-accent-text">{entry.request.method}</span>
                      <Badge tone={statusTone(entry.response.status)} className="font-mono">{entry.response.status}</Badge>
                      <span className="ml-auto font-mono text-[11px] tabular-nums text-muted-foreground">{entry.timestamp.toLocaleTimeString()}</span>
                    </div>
                    <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{entry.request.url}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default ProxyPage;
